import { SortingState } from "@tanstack/react-table"
import { useState } from "react"

import getObjectCardsViewConfigStorageKey from "@utils/getObjectCardsViewConfigStorageKey"
import getObjectTableViewConfigStorageKey from "@utils/getObjectTableViewConfigStorageKey"

import { ScheduleHorizon } from "@constants/scheduleHorizon"

import useCardsViewConfig from "./CardsView/hooks/useCardsViewConfig"
import { ObjectsViewContext } from "./ObjectsView.context"
import { ObjectsData, ObjectsViewContextValues, ObjectsViewProviderProps } from "./ObjectsView.types"
import useTableViewConfig from "./TableView/hooks/useTableViewConfig"
import useViewTypesProvider from "./ViewTypesProvider/ViewTypesProvider.context"
import useObjectsViewData from "./hooks/useObjectsViewData"
import useObjectsViewSortingState from "./hooks/useObjectsViewSortingState"
import useObjectsViewState from "./hooks/useObjectsViewState"
import useScheduleHorizon from "./hooks/useScheduleHorizon"
import useUnfilteredTabCount from "./hooks/useUnfilteredTabCount"

// The list every view renders: phases/tabs/search state, the infinite query, and the settings that follow
// the active view type (sort, schedule horizon). View-specific machinery layers on top in TableView.Provider
// and CardsView.Provider.
export default function ObjectsViewProvider<T = ObjectsData>(props: ObjectsViewProviderProps) {
    const {
        children,
        activePhaseStorageKey,
        activeTabStorageKey,
        searchKeywordsStorageKey,
        listTitle,
        listDataFilters,
        objectType,
        objectTypeVariation,
        objectName,
        objectCreateRoute,
        objectIndexName,
        defaultActivePhase,
        isRowClickable,
        isEmptyStateCreateButtonVisible,
        onRowHoverChange,
    } = props

    const {
        activePhase,
        activeTab,
        searchKeywords,
        filters,
        setActivePhase,
        setActiveTab,
        setSearchKeywords,
        setFilters,
    } = useObjectsViewState({
        activePhaseStorageKey,
        activeTabStorageKey,
        searchKeywordsStorageKey,
        defaultActivePhase,
        defaultFilters: listDataFilters,
    })

    // The view menu's open state lives here so the list edge states can open it too.
    const [isViewMenuOpen, setIsViewMenuOpen] = useState<boolean>(false)

    const tabContentName = `${activeTab?.title} ${listTitle}`

    const viewConfigStorageKeyArgs = {
        objectType: objectType,
        objectTypeVariation: objectTypeVariation,
        phase: activePhase,
        tab: activeTab?.title ?? "",
    }

    const { currentTableViewConfig, updateCurrentTableViewConfig, getTableViewConfig } = useTableViewConfig(
        getObjectTableViewConfigStorageKey(viewConfigStorageKeyArgs),
    )
    const { currentCardsViewConfig, updateCardsViewConfig, getCardsViewConfig } = useCardsViewConfig(
        getObjectCardsViewConfigStorageKey(viewConfigStorageKeyArgs),
    )

    // Each view type remembers its sort in its own config, so the same tab can be ordered one way as a
    // table and another way as cards. A view with no config of its own runs on the tab's default
    // ordering and has nowhere to keep a change.
    const { activeViewType } = useViewTypesProvider()
    let savedSorting: SortingState | undefined
    let saveSorting: ((sorting: SortingState) => void) | undefined
    if (activeViewType === "table") {
        savedSorting = currentTableViewConfig?.sorting
        saveSorting = (sorting) => updateCurrentTableViewConfig("sorting", sorting)
    } else if (activeViewType === "cards") {
        savedSorting = currentCardsViewConfig?.sorting
        saveSorting = (sorting) => updateCardsViewConfig("sorting", sorting)
    }

    const {
        sorting,
        onSortingChange,
        setSortingChanged,
        sortingChanged,
        setCurrentSortToAscending,
        setCurrentSortToDescending,
        onSortingItemSelection,
    } = useObjectsViewSortingState<T>({
        activeTab,
        savedSorting,
        saveSorting,
    })

    // The schedule horizon is a view setting like the sort, so each view type keeps its own per tab.
    let savedScheduleHorizon: ScheduleHorizon | undefined
    let saveScheduleHorizon: ((horizon: ScheduleHorizon) => void) | undefined
    let getSavedScheduleHorizonForTab: ((tab: string) => ScheduleHorizon | undefined) | undefined
    if (activeViewType === "table") {
        savedScheduleHorizon = currentTableViewConfig?.scheduleHorizon
        saveScheduleHorizon = (horizon) => updateCurrentTableViewConfig("scheduleHorizon", horizon)
        getSavedScheduleHorizonForTab = (tab) =>
            getTableViewConfig(getObjectTableViewConfigStorageKey({ ...viewConfigStorageKeyArgs, tab }))
                ?.scheduleHorizon
    } else if (activeViewType === "cards") {
        savedScheduleHorizon = currentCardsViewConfig?.scheduleHorizon
        saveScheduleHorizon = (horizon) => updateCardsViewConfig("scheduleHorizon", horizon)
        getSavedScheduleHorizonForTab = (tab) =>
            getCardsViewConfig(getObjectCardsViewConfigStorageKey({ ...viewConfigStorageKeyArgs, tab }))
                ?.scheduleHorizon
    }

    const {
        scheduleHorizon,
        setScheduleHorizon,
        isScheduleHorizonAvailable,
        scheduleHorizonUpperBound,
        listFilters,
        getFiltersForTab,
    } = useScheduleHorizon({
        objectType,
        activePhase,
        savedScheduleHorizon,
        saveScheduleHorizon,
        getSavedScheduleHorizonForTab,
        filters,
    })

    const {
        currentData,
        error,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
        isEmpty,
    } = useObjectsViewData<T>({
        activeTab,
        tabContentName,
        searchKeywords,
        filters: listFilters,
        sorting,
    })

    // An empty list with a horizon set and rows behind it is the horizon's doing, not an empty tab.
    const { unfilteredTabCount, isUnfilteredTabCountLoading } = useUnfilteredTabCount({
        activeTab,
        tabContentName,
        searchKeywords,
        filters,
        isEnabled: isEmpty && !!scheduleHorizonUpperBound,
    })
    const hiddenByScheduleHorizonCount = unfilteredTabCount ?? 0
    const isHiddenByScheduleHorizon = isEmpty && !!scheduleHorizonUpperBound && hiddenByScheduleHorizonCount > 0

    // The list stays loading until that count settles, so the plain empty state never shows first.
    const isListLoading = isLoading || isUnfilteredTabCountLoading
    const isListEmpty = isEmpty && !isUnfilteredTabCountLoading

    const contextValue: ObjectsViewContextValues<T> = {
        data: currentData,
        error,
        isLoading: isListLoading,
        isError,
        isEmpty: isListEmpty,
        isSearching: !!searchKeywords,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        activePhase,
        setActivePhase,
        activeTab,
        setActiveTab,
        searchKeywords,
        setSearchKeywords,
        filters: listFilters,
        setFilters,
        getFiltersForTab,
        tabContentName,
        sorting,
        onSortingChange,
        sortingChanged,
        setSortingChanged,
        setCurrentSortToAscending,
        setCurrentSortToDescending,
        onSortingItemSelection,
        scheduleHorizon,
        setScheduleHorizon,
        isScheduleHorizonAvailable,
        isHiddenByScheduleHorizon,
        hiddenByScheduleHorizonCount,
        isViewMenuOpen,
        setIsViewMenuOpen,
        objectType,
        objectTypeVariation,
        objectName,
        objectIndexName,
        objectCreateRoute,
        isRowClickable,
        isEmptyStateCreateButtonVisible,
        onRowHoverChange,
    }

    return <ObjectsViewContext.Provider value={contextValue}>{children}</ObjectsViewContext.Provider>
}
